import { useRef, useState } from 'react'
import { api } from '../api'
import { openExternal, siteUrl } from '../discord/sdk'
import { useI18n } from '../i18n'
import { Icon } from '../ui/Icon'
import { Popover } from '../ui/Popover'

/**
 * In Discord: the room's link for people without the activity, and a way to
 * continue in one's own browser, where full screen is allowed.
 */
export function BrowserLink({ roomId, name }: { roomId: string; name: string }) {
  const { t } = useI18n()
  const anchor = useRef<HTMLButtonElement>(null)
  const input = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const link = `${siteUrl()}/r/${roomId}`

  const copy = async () => {
    if (await copyText(link)) {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } else {
      // Nothing to copy with: the link is selected for Ctrl+C.
      input.current?.select()
    }
  }

  const continueThere = async () => {
    setBusy(true)
    setFailed(false)
    try {
      const { code } = await api.handoff()
      // The code goes in the fragment: it never reaches server logs.
      openExternal(`${link}#login=${encodeURIComponent(code)}`)
      setOpen(false)
    } catch {
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <button ref={anchor} type="button" className="btn btn--small" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <Icon name="external" />
        <span className="btn__label">{t('room.browser')}</span>
      </button>
      <Popover anchor={anchor} open={open} onClose={() => setOpen(false)} side="bottom" className="browser-link" label={t('room.browserAria')}>
        <p className="popover__title">{t('room.browserLink')}</p>
        <div className="browser-link__row">
          <input ref={input} readOnly value={link} onFocus={(e) => e.currentTarget.select()} aria-label={t('room.browserLink')} />
          <button type="button" className="browser-link__copy" onClick={copy} aria-label={t('room.copy')} title={t('room.copy')}>
            <Icon name={copied ? 'check' : 'link'} />
          </button>
        </div>
        <p className="browser-link__hint muted">{t('room.browserLinkHint')}</p>
        <button type="button" className="browser-link__open" onClick={continueThere} disabled={busy}>
          <span>
            <Icon name="external" /> {t('room.openInBrowser')}
          </span>
          <span className="muted">{failed ? t('room.handoffFailed') : t('room.openInBrowserHint', { name })}</span>
        </button>
      </Popover>
    </>
  )
}

/**
 * Copies text. Discord's frame does not allow the Clipboard API, so the
 * older command is tried as well.
 */
async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    // fall through
  }
  const area = document.createElement('textarea')
  area.value = text
  area.setAttribute('readonly', '')
  area.style.position = 'fixed'
  area.style.opacity = '0'
  document.body.append(area)
  area.select()
  try {
    return document.execCommand('copy')
  } catch {
    return false
  } finally {
    area.remove()
  }
}
